import {
  Alignment,
  FloatingArrow,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
} from "@floating-ui/react";
import React, { useMemo, useRef, useState } from "react";
import { useIsRtl } from "@app/hooks";

type Side = "top" | "side-start" | "bottom" | "side-end";
type AlignedPlacement = `${Side}-${Alignment}`;
type Placement = Side | AlignedPlacement;

interface TooltipProps {
  placement?: Placement;
  tooltip: React.ReactNode;
  children: React.ReactElement;
}

export function Tooltip({
  children,
  placement = "top",
  tooltip,
}: TooltipProps) {
  const isRtl = useIsRtl();
  const arrowRef = useRef(null);
  const [open, setOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    placement: placement
      .replace("side-start", isRtl ? "left" : "right")
      .replace("side-end", isRtl ? "right" : "left") as any,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(10),
      flip({
        crossAxis: placement.includes("-"),
        fallbackAxisSideDirection: "start",
        padding: 5,
      }),
      shift({ padding: 20 }),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const hover = useHover(context, {
    move: false,
    enabled: !!tooltip,
  });

  const focus = useFocus(context, { enabled: !!tooltip });
  const role = useRole(context, { role: "tooltip", enabled: !!tooltip });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    role,
  ]);

  const { isMounted, styles } = useTransitionStyles(context, { duration: 100 });
  const childRef = (
    children as React.ReactElement & { ref?: React.Ref<unknown> }
  ).ref;
  const canAttachReferenceRef =
    typeof children.type === "string" ||
    (typeof children.type === "object" &&
      children.type !== null &&
      "$$typeof" in children.type);
  const referenceProps = getReferenceProps({
    ...children.props,
    ref: canAttachReferenceRef
      ? (node: HTMLElement | null) => {
          refs.setReference(node);

          if (typeof childRef === "function") {
            childRef(node);
          } else if (childRef && "current" in childRef) {
            (childRef as React.MutableRefObject<HTMLElement | null>).current =
              node;
          }
        }
      : undefined,
  });
  const referenceNode = useMemo(
    () =>
      canAttachReferenceRef ? (
        React.cloneElement(children, referenceProps)
      ) : (
        <span
          className="inline-flex"
          ref={refs.setReference}
          {...referenceProps}
        >
          {children}
        </span>
      ),
    [canAttachReferenceRef, children, referenceProps, refs.setReference],
  );

  return (
    <>
      {referenceNode}
      {isMounted && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              ...styles,
              maxWidth: "calc(100vw - 40px)",
            }}
            {...getFloatingProps()}
            className="z-40 min-w-[150px] whitespace-pre-wrap rounded-md bg-popover px-4 py-2 text-center text-popover-foreground shadow-lg ring-1 ring-border"
          >
            {tooltip}
            <FloatingArrow
              ref={arrowRef}
              context={context}
              fill="hsl(var(--popover))"
              stroke="hsl(var(--border))"
              strokeWidth={1}
              tipRadius={2}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
